// src/integrations/player-core/index.ts
import { DestroyableSet as DestroyableSet6 } from "@nrk/destroyable";
import { CoreEvent as CoreEvent7, version as playerCoreVersion } from "@nrk/player-core/types.js";
import { addLogHandler } from "@nrk/player-core/utils.js";
import { getLogger as getLogger5 } from "@nrk/player-core/utils.js";
import { NrkPlayerTracker } from "@nrk/snowplow-web";
import cuid2 from "cuid";

// src/tracking-apis/app-insights/app-insights.ts
import { getLogger } from "@nrk/player-core/utils.js";

// src/tracking-apis/app-insights/format-utils.ts
var MaxLength = {
  ContextTagValue: 1024,
  CustomPropertyKey: 150,
  CustomPropertyValue: 8192,
  EventDataName: 150,
  ExceptionMessage: 1024,
  ExceptionStack: 32768,
  ExceptionTypeName: 150,
  InstrumentationKey: 128,
  MetricName: 150
};
var maxLength = (value, maxLen) => value.substring(0, maxLen);
function formatProperties(rawProperties) {
  const properties = {};
  let uniqueKeyCounter = 0;
  if (rawProperties !== void 0) {
    for (const key of Object.keys(rawProperties)) {
      const value = rawProperties[key];
      if (value !== void 0) {
        let truncatedKey = maxLength(key, MaxLength.CustomPropertyKey);
        if (truncatedKey in properties) {
          truncatedKey = `${maxLength(truncatedKey, MaxLength.CustomPropertyKey - 3)}${d03(uniqueKeyCounter++)}`;
        }
        properties[truncatedKey] = maxLength(stringify(value), MaxLength.CustomPropertyValue);
      }
    }
  }
  return properties;
}
function formatTags(contextTags) {
  const tags = {};
  if (contextTags !== void 0) {
    for (const key of Object.keys(contextTags)) {
      const value = contextTags[key];
      if (value !== void 0) {
        tags[key] = maxLength(value, MaxLength.ContextTagValue);
      }
    }
  }
  return tags;
}
function formatException(exception, causedExceptions) {
  const errorObj = typeof exception === "object" && exception !== null ? toPlainObject(exception) : { message: `${exception}` };
  const stack = stringify(errorObj.stack);
  const truncatedStack = maxLength(stack, MaxLength.ExceptionStack);
  const hasFullStack = truncatedStack.length === stack.length;
  const details = {
    typeName: maxLength(stringify(errorObj.name ?? "Error"), MaxLength.ExceptionTypeName),
    message: maxLength("message" in errorObj ? stringify(errorObj.message) : `${errorObj}`, MaxLength.ExceptionMessage),
    stack: truncatedStack,
    hasFullStack,
    parsedStack: []
  };
  if (causedExceptions !== void 0) {
    const caused = causedExceptions[causedExceptions.length - 1];
    if (caused.id === void 0) {
      caused.id = causedExceptions.length;
    }
    details.outerId = caused.id;
  }
  const list = causedExceptions ?? [];
  list.push(details);
  if ("cause" in errorObj) {
    formatException(errorObj.cause, list);
  }
  return list;
}
function formatTime() {
  return (/* @__PURE__ */ new Date()).toISOString();
}
function stringify(value) {
  if (typeof value !== "object" || value === null) {
    return `${value}`;
  }
  const plainObject = toPlainObject(value);
  return JSON.stringify(plainObject);
}
function toPlainObject(obj) {
  const result = {};
  Object.getOwnPropertyNames(obj).concat(["name", "message", "stack", "msExtendedCode", "cause"]).filter((prop) => !/^(\d+|[A-Z_]+)$/.exec(prop)).filter((prop) => obj[prop] !== void 0).forEach((prop) => {
    if (typeof obj[prop] === "object" && obj[prop] !== null) {
      result[prop] = toPlainObject(obj[prop]);
    } else {
      result[prop] = obj[prop];
    }
  });
  if (isMediaError(obj)) {
    result.name = "MediaError";
  }
  return result;
}
function isMediaError(e) {
  return typeof e === "object" && e !== null && e.name === void 0 && e.MEDIA_ERR_ABORTED === 1 && typeof e.code === "number";
}
function d03(value) {
  const str = `00${value}`;
  return str.substring(str.length - 3);
}

// src/tracking-apis/app-insights/friendly-context-tags.ts
var irregularKeys = {
  applicationId: "ai.application.applicationId",
  applicationVersion: "ai.application.ver",
  userAgent: "ai.user.userAgent",
  userAnonymousUserAcquisitionDate: "ai.user.anonUserAcquisitionDate",
  userAuthenticatedUserAcquisitionDate: "ai.user.authUserAcquisitionDate"
};
function translateTagKey(key) {
  const irregularKey = irregularKeys[key];
  if (irregularKey !== void 0) {
    return irregularKey;
  }
  const items = key.split(/([A-Z]+$)|([A-Z]+)([A-Z])|([A-Z])/).filter((s) => s !== void 0);
  items[1] = `.${items[1].toLowerCase()}`;
  return `ai.${items.join("")}`;
}
function translateTagKeys(tags) {
  if (tags === void 0) {
    return void 0;
  }
  const translatedTags = {};
  for (const key of Object.keys(tags)) {
    translatedTags[translateTagKey(key)] = tags[key];
  }
  return translatedTags;
}

// src/tracking-apis/app-insights/output-types.ts
var DataPointType = /* @__PURE__ */ ((DataPointType2) => {
  DataPointType2[DataPointType2["Measurement"] = 0] = "Measurement";
  DataPointType2[DataPointType2["Aggregation"] = 1] = "Aggregation";
  return DataPointType2;
})(DataPointType || {});
var SeverityLevel = /* @__PURE__ */ ((SeverityLevel2) => {
  SeverityLevel2[SeverityLevel2["Verbose"] = 0] = "Verbose";
  SeverityLevel2[SeverityLevel2["Information"] = 1] = "Information";
  SeverityLevel2[SeverityLevel2["Warning"] = 2] = "Warning";
  SeverityLevel2[SeverityLevel2["Error"] = 3] = "Error";
  SeverityLevel2[SeverityLevel2["Critical"] = 4] = "Critical";
  return SeverityLevel2;
})(SeverityLevel || {});

// src/tracking-apis/app-insights/app-insights.ts
var DEFAULT_ENDPOINT = "https://dc.services.visualstudio.com";
var DEFAULT_PATH = "/v2/track";
var logger = getLogger("AppInsights");
var AppInsights = class {
  iKey;
  batch = [];
  maxBatchInterval = 1e3;
  maxBatchSize = 100;
  batchTimeout;
  samplingPercentage = 100;
  sampledIn = true;
  constructor(instrumentationKey, config) {
    if (maxLength(instrumentationKey, MaxLength.InstrumentationKey) !== instrumentationKey) {
      throw new Error("Bad instrumentation key. Too long.");
    }
    this.iKey = instrumentationKey;
    if (config?.maxBatchInterval !== void 0) {
      this.maxBatchInterval = config.maxBatchInterval;
    }
    if (config?.maxBatchSize !== void 0) {
      this.maxBatchSize = config.maxBatchSize;
    }
    if (config?.samplingPercentage !== void 0 && config.samplingPercentage < 100 && config.samplingPercentage >= 0) {
      this.samplingPercentage = config.samplingPercentage;
      this.sampledIn = Math.random() * 100 < config.samplingPercentage;
    }
  }
  trackEvent(name, properties, tags) {
    const event = {
      ver: 1,
      name: `Microsoft.ApplicationInsights.${this.eventNameKey}.Event`,
      data: {
        baseType: "EventData",
        baseData: {
          ver: 2,
          name: maxLength(name, MaxLength.EventDataName),
          properties: formatProperties(properties)
        }
      },
      iKey: this.iKey,
      sampleRate: this.samplingPercentage,
      tags: formatTags(translateTagKeys(tags)),
      time: formatTime()
    };
    this.queueEvent(event);
  }
  trackException(exception, severityLevel, properties, tags) {
    const event = {
      ver: 1,
      name: `Microsoft.ApplicationInsights.${this.eventNameKey}.Exception`,
      data: {
        baseType: "ExceptionData",
        baseData: {
          ver: 2,
          exceptions: formatException(exception),
          severityLevel: severityLevel ?? 3 /* Error */,
          properties: formatProperties(properties)
        }
      },
      iKey: this.iKey,
      sampleRate: this.samplingPercentage,
      tags: formatTags(translateTagKeys(tags)),
      time: formatTime()
    };
    this.queueEvent(event);
  }
  trackMetric(name, value, properties, tags) {
    const dataPoint = typeof value === "number" ? {
      name: maxLength(name, MaxLength.MetricName),
      kind: 0 /* Measurement */,
      value
    } : {
      name: maxLength(name, MaxLength.MetricName),
      kind: 1 /* Aggregation */,
      ...value
    };
    const event = {
      ver: 1,
      name: `Microsoft.ApplicationInsights.${this.eventNameKey}.Metric`,
      data: {
        baseType: "MetricData",
        baseData: {
          ver: 2,
          metrics: [dataPoint],
          properties: formatProperties(properties)
        }
      },
      iKey: this.iKey,
      // Don't rate limit metric events. This is consistent with the
      // Application Insights Node SDK. Does it have to be this way?
      sampleRate: 100,
      tags: formatTags(translateTagKeys(tags)),
      time: formatTime()
    };
    this.queueEvent(event);
  }
  flush() {
    if (this.batch.length === 0) {
      return;
    }
    const batch = this.batch;
    this.batch = [];
    this.sendReport(batch).catch((error) => {
      logger.error(`Failed to send ${batch.length} events:`, error);
    });
  }
  get eventNameKey() {
    return this.iKey.replace(/-/g, "");
  }
  queueEvent(event) {
    if (event.sampleRate !== 100 && !this.sampledIn) {
      return;
    }
    this.batch.push(event);
    if (this.batch.length >= this.maxBatchSize) {
      this.flush();
    } else {
      if (this.batchTimeout !== void 0) {
        clearTimeout(this.batchTimeout);
      }
      this.batchTimeout = setTimeout(() => this.flush(), this.maxBatchInterval);
    }
  }
  async sendReport(report) {
    const url = `${DEFAULT_ENDPOINT}${DEFAULT_PATH}`;
    const response = await fetch(url, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json"
      },
      body: JSON.stringify(report)
    });
    if (!response.ok) {
      throw new Error(`Unexpected response code: ${response.status}`);
    }
  }
};

// src/tracking-apis/app-tracker.ts
import { DestroyableSet as DestroyableSet2 } from "@nrk/destroyable";
import { getLogger as getLogger2 } from "@nrk/player-core/utils.js";
import cuid from "cuid";

// src/utils/before-unload.ts
import { destroyableEventListener, DestroyableSet } from "@nrk/destroyable";
function beforeUnload(callback) {
  const listeners = new DestroyableSet();
  listeners.addDestroyables([
    destroyableEventListener(window, "beforeunload", () => {
      listeners.destroy();
      callback();
    }),
    destroyableEventListener(window, "pagehide", () => {
      listeners.destroy();
      callback();
    })
  ]);
  return listeners;
}

// src/tracking-apis/app-tracker.ts
var logger2 = getLogger2("AppTracker");
var AppTracker = class extends DestroyableSet2 {
  constructor(appInsights, applicationName, customData) {
    super();
    this.appInsights = appInsights;
    this.applicationName = applicationName;
    this.customData = customData;
    this.baseFixedTags = {
      deviceId: "browser",
      deviceType: "Browser",
      sessionId: cuid(),
      userId: getAnonymousUserId()
    };
    this.baseDynamicTags = () => ({
      operationName: window.location.pathname
    });
    this.addEventListener("destroy", () => {
      this.customData = void 0;
    });
    this.addDestroyable(beforeUnload(() => this.appInsights.flush()));
  }
  baseFixedTags;
  baseDynamicTags;
  updateFixedProperties(properties) {
    if (this.customData === void 0) {
      this.customData = { fixedProperties: properties };
    } else if (this.customData.fixedProperties === void 0) {
      this.customData.fixedProperties = properties;
    } else {
      Object.assign(this.customData.fixedProperties, properties);
    }
  }
  trackEvent(name, props) {
    const properties = this.collectProperties(props);
    const tags = this.collectTags();
    logger2.debug("Sending event:", name, properties, tags);
    this.appInsights.trackEvent(name, properties, tags);
  }
  trackMetric(name, value, props) {
    const properties = this.collectProperties(props);
    const tags = this.collectTags();
    logger2.debug("Sending metric:", name, value, properties, tags);
    this.appInsights.trackMetric(name, value, properties, tags);
  }
  trackException(exception, fatal, props) {
    const properties = this.collectProperties(props);
    const tags = this.collectTags();
    const severityLevel = fatal ? 3 /* Error */ : 2 /* Warning */;
    logger2.debug("Sending exception:", exception, severityLevel, properties, tags);
    this.appInsights.trackException(exception, severityLevel, properties, tags);
  }
  trackQoSEvent(props) {
    const name = "nativePlayerQos";
    const tags = this.collectTags();
    const properties = this.collectProperties(props);
    delete properties.playback;
    delete properties.mediaInfo;
    delete properties.displayModes;
    properties.properties.applicationName = properties.applicationName;
    properties.properties.applicationVersion = properties.applicationVersion;
    logger2.debug("Sending QoS event:", name, properties, tags);
    this.appInsights.trackEvent(name, properties, tags);
  }
  trackPlaybackRetry(props) {
    const name = "playbackRetry";
    const tags = this.collectTags();
    const properties = this.collectProperties(props);
    logger2.debug("Sending PlaybackRetry event:", name, properties, tags);
    this.appInsights.trackEvent(name, properties, tags);
  }
  flush() {
    this.appInsights.flush();
  }
  collectProperties(props) {
    return filterUndefined({
      applicationName: this.applicationName,
      ...this.customData?.fixedProperties,
      ...this.customData?.dynamicProperties?.(),
      ...props
    });
  }
  collectTags() {
    const tags = {
      ...this.baseFixedTags,
      ...this.baseDynamicTags(),
      ...this.customData?.fixedTags,
      ...this.customData?.dynamicTags?.()
    };
    if (tags.userId === void 0) {
      tags.userId = tags.sessionId;
    }
    return filterUndefined(tags);
  }
};
function filterUndefined(obj) {
  return Object.keys(obj).reduce((o, k) => {
    const v = obj[k];
    if (v !== void 0) {
      o[k] = v;
    }
    return o;
  }, {});
}
function getAnonymousUserId() {
  const key = "nrk.player.anonymousUser";
  try {
    let userId = window.localStorage.getItem(key);
    if (userId === null) {
      userId = cuid();
      window.localStorage.setItem(key, userId);
    }
    return userId;
  } catch (err) {
    logger2.log("Failed to store/retrieve anonymous user id:", err);
    return void 0;
  }
}

// src/tracking-apis/progress-tracker.ts
var ProgressTracker = class {
  programId;
  playbackStartTime = 0;
  finalized = false;
  trackingCallback;
  constructor(programId, playbackStartTime, callback) {
    this.programId = programId;
    this.playbackStartTime = playbackStartTime;
    this.trackingCallback = callback;
  }
  /**
   * Report playback progress for the current program id.
   *
   * @param playbackStartTime number
   * @param timePlayed number
   */
  reportPlaybackProgress(playbackStartTime, timePlayed) {
    if (!this.finalized) {
      this.playbackStartTime = playbackStartTime;
      this.trackingCallback({ playbackStartTime, timePlayed, programId: this.programId });
    }
  }
  /**
   * Last progress reporting done to make the episode be marked as watched.
   * Triggered on PlayerEvents.PlayNextEpisode event
   */
  finalizeProgressReporting(timePlayed) {
    if (timePlayed) {
      this.trackingCallback({ playbackStartTime: this.playbackStartTime, timePlayed, programId: this.programId });
      this.finalized = true;
    }
  }
};

// src/utils/time-range.ts
function summarizeRanges(ranges) {
  return ranges.reduce((sum, range) => sum + range.end - range.start, 0);
}
function clipRanges(ranges, targetRange) {
  if (!targetRange) {
    return ranges;
  }
  const result = [];
  for (let i = 0; i < ranges.length; ++i) {
    const range = ranges[i];
    if (range.end > targetRange.start && range.start < targetRange.end) {
      result.push({ start: Math.max(targetRange.start, range.start), end: Math.min(targetRange.end, range.end) });
    }
  }
  return result;
}
function mergeOverlappingRanges(ranges) {
  if (ranges.length === 0) {
    return [];
  }
  ranges.sort((a, b) => a.start - b.start);
  const mergedRanges = [ranges[0]];
  let currentMergeRange = 0;
  for (let i = 1; i < ranges.length; ++i) {
    const range = ranges[i];
    if (range.start > mergedRanges[currentMergeRange].end) {
      mergedRanges.push(range);
      currentMergeRange++;
    } else if (range.end > mergedRanges[currentMergeRange].end) {
      mergedRanges[currentMergeRange].end = range.end;
    }
  }
  return mergedRanges;
}

// src/utils/played-time-collector.ts
var PlayedTimeCollector = class {
  playedRanges;
  playedRangesSum;
  totalPlayingTimeListeners;
  startPosition;
  currentPosition;
  constructor() {
    this.playedRanges = [];
    this.playedRangesSum = 0;
    this.totalPlayingTimeListeners = [];
  }
  /**
   * Tell the collector that time just started running at the given position.
   * This marks the start of a played time range.
   */
  timeStarted(startPosition) {
    if (this.startPosition === void 0) {
      this.startPosition = startPosition;
    }
  }
  /**
   * Tell the collector that time just stopped running at the given position.
   * This completes the played range.
   */
  timeStopped(endPosition) {
    if (this.startPosition !== void 0) {
      const startPosition = this.startPosition;
      if (endPosition > startPosition) {
        const range = { start: startPosition, end: endPosition };
        const rangeDuration = endPosition - startPosition;
        this.playedRanges.push(range);
        this.playedRangesSum += rangeDuration;
        this.checkTimeListeners(this.playedRangesSum);
      }
      this.startPosition = void 0;
      this.currentPosition = void 0;
    }
  }
  /**
   * Tell the collector where the playhead position is right now. It should
   * typically be after the start position when time started running.
   */
  timeUpdate(currentPosition) {
    if (this.startPosition !== void 0 && currentPosition >= this.startPosition) {
      this.currentPosition = currentPosition;
      const totalPlayingTime = this.playedRangesSum + currentPosition - this.startPosition;
      this.checkTimeListeners(totalPlayingTime);
    }
  }
  /**
   * Be notified when total playing time passes a certain number of seconds. If
   * the time has already passed, then the listener is called immediately.
   */
  onceTotalPlayingTime(time, handler, context = this) {
    const totalPlayingTime = this.getTotalPlayingTime();
    if (time <= totalPlayingTime) {
      handler.call(context, totalPlayingTime);
    } else {
      this.totalPlayingTimeListeners.push({ time, handler, context });
      this.totalPlayingTimeListeners.sort((a, b) => a.time - b.time);
    }
  }
  /**
   * Returns the total time the player has been playing in the current session.
   */
  getTotalPlayingTime() {
    if (this.startPosition !== void 0 && this.currentPosition !== void 0) {
      return this.playedRangesSum + this.currentPosition - this.startPosition;
    }
    return this.playedRangesSum;
  }
  /**
   * Returns the number of seconds of the media or range that has been played.
   */
  getDurationOfMediaPlayed(targetRange) {
    const ranges = [...this.playedRanges];
    if (this.startPosition !== void 0 && this.currentPosition !== void 0) {
      ranges.push({ start: this.startPosition, end: this.currentPosition });
    }
    return summarizeRanges(mergeOverlappingRanges(clipRanges(ranges, targetRange)));
  }
  destroy() {
    this.totalPlayingTimeListeners = [];
    this.playedRanges = [];
  }
  checkTimeListeners(totalPlayingTime) {
    while (this.totalPlayingTimeListeners.length > 0) {
      if (this.totalPlayingTimeListeners[0].time <= totalPlayingTime) {
        const listener = this.totalPlayingTimeListeners.shift();
        listener?.handler.call(listener.context, totalPlayingTime);
      } else {
        break;
      }
    }
  }
};

// src/utils/site-config.ts
var siteConfigs = [
  {
    hostname: "^[^.]*\\btv\\b[^.]*\\.nrk\\.no$",
    data: {
      name: "NRK TV"
    }
  },
  {
    hostname: "^[^.]*\\bradio\\b[^.]*\\.nrk\\.no$",
    data: {
      name: "NRK Radio"
    }
  },
  {
    hostname: "^[^.]*\\btv\\b[^.]*\\.nrksuper\\.no$",
    data: {
      name: "NRK Super TV"
    }
  },
  {
    hostname: "^(.+\\.)?nrksuper\\.no$",
    data: {
      name: "NRK Super"
    }
  },
  {
    hostname: "^(.+\\.)?p3\\.no$",
    data: {
      name: "NRK P3"
    }
  },
  {
    hostname: "^(.+\\.)?nrkbeta\\.no$",
    data: {
      name: "NRK beta"
    }
  },
  {
    hostname: "^(.+\\.)?yr\\.no$",
    data: {
      name: "Yr"
    }
  },
  {
    hostname: "\\b(ark|felles\\.ds|preview|fil|kubeint|snutt|kurator)\\.nrk\\.no$",
    data: {
      environment: "internal"
    }
  },
  {
    hostname: "^static\\.nrk\\.no$",
    pathname: "^/dev/",
    data: {
      environment: "dev"
    }
  },
  {
    hostname: "(www|static)\\.nrk\\.no$",
    pathname: "^(/embed/.*|.*/video-embed(\\.html)?)$",
    data: {
      isVideoEmbed: true
    }
  },
  {
    hostname: "\\.nrk\\.no$",
    data: {
      name: "NRK.no",
      options: {
        applicationInsights: {
          samplingPercentage: 10
        }
      }
    }
  },
  {
    hostname: "^localhost$",
    data: {
      environment: "test"
    }
  },
  {
    hostname: "\\.nrk\\.cloud$",
    data: {
      environment: "test"
    }
  },
  {
    // Internal domains without dots.
    hostname: "^[A-Za-z0-9]+$",
    data: {
      environment: "internal"
    }
  },
  {
    // NRK IP ranges.
    hostname: "^160\\.6[78]\\.\\d+\\.\\d+$",
    data: {
      environment: "internal"
    }
  },
  {
    // Assume all IP addresses are for testing.
    hostname: "^\\d+\\.\\d+\\.\\d+\\.\\d+$",
    data: {
      environment: "test"
    }
  },
  {
    hostname: "^nrkno\\.github\\.io$",
    data: {
      environment: "test"
    }
  }
];

// src/utils/site.ts
var ENVIRONMENT_EMBED_DIRECTLY = "embed-test";
var Site = class {
  hasParent;
  localSite;
  parentSite;
  constructor(config) {
    const {
      parentURL = document.referrer,
      localLocation = window.location,
      hasParent = window.top !== window.self
    } = config || {};
    this.hasParent = hasParent;
    this.localSite = this.detectSite(localLocation) || {};
    this.parentSite = {};
    if (hasParent && parentURL) {
      const parsedURL = this.parseURL(parentURL);
      if (parsedURL) {
        this.parentSite = this.detectSite(parsedURL);
      }
    }
  }
  get isEmbedded() {
    return !!(this.hasParent && this.localSite.isVideoEmbed);
  }
  get environment() {
    if (this.parentSite.environment) {
      return this.parentSite.environment;
    }
    if (this.localSite.environment) {
      return this.localSite.environment;
    }
    if (!this.hasParent && this.localSite.isVideoEmbed) {
      return ENVIRONMENT_EMBED_DIRECTLY;
    }
    return void 0;
  }
  get isProduction() {
    return !this.environment;
  }
  get name() {
    const name = this.parentSite.name || this.localSite.name;
    if (!name || !this.isProduction) {
      if (this.parentSite.location && this.parentSite.location.hostname) {
        return this.parentSite.location.hostname;
      }
      if (this.isEmbedded) {
        return "N/A";
      }
      if (this.localSite.location && this.localSite.location.hostname) {
        return this.localSite.location.hostname;
      }
      return "N/A";
    }
    return name;
  }
  get fullName() {
    const tags = [];
    if (this.environment && (this.parentSite.configuredEnvironment || this.localSite.configuredEnvironment || this.environment === ENVIRONMENT_EMBED_DIRECTLY)) {
      tags.push(this.environment);
    }
    if (this.isEmbedded) {
      tags.push("embed");
    }
    if (tags.length) {
      return `${this.name} (${tags.join(", ")})`;
    }
    return this.name;
  }
  get options() {
    return this.parentSite.options || this.localSite.options || {};
  }
  // Parsing/detection
  parseURL(url) {
    return new URL(url);
  }
  detectSite(location) {
    const site = { location };
    const { hostname, pathname } = location;
    const data = this.matchSite(hostname, pathname);
    if (data) {
      Object.assign(site, data);
      if (site.environment) {
        site.configuredEnvironment = true;
      } else {
        const environment = this.matchEnvironment(hostname);
        if (environment) {
          site.environment = environment;
          site.options = void 0;
        }
      }
    }
    return site;
  }
  matchSite(hostname, pathname) {
    const siteConfig = siteConfigs.find((patterns) => {
      return new RegExp(patterns.hostname).test(hostname) && (patterns.pathname ? new RegExp(patterns.pathname).test(pathname) : true);
    });
    return siteConfig ? siteConfig.data : void 0;
  }
  matchEnvironment(hostname) {
    const match = /\b(dev|preprod|nonprod|stage|test)\b/.exec(hostname);
    return match ? match[1] : void 0;
  }
};

// src/integrations/player-core/app-insights.ts
import { destroyableEventListener as destroyableEventListener2, DestroyableSet as DestroyableSet3, destroyableTimeout } from "@nrk/destroyable";
import { CoreEvent, PausedReason } from "@nrk/player-core/types.js";
var STILL_WAITING_TIMEOUT = 20;
function trackAppInsightsSession(session, appTracker) {
  const destroyables = new DestroyableSet3();
  const eventPrefix = "player-core";
  let waiting;
  session.once(CoreEvent.MediaInfoLoaded, () => {
    appTracker.trackEvent(`${eventPrefix}-media-info-loaded`);
  });
  session.on(CoreEvent.PlayingChange, (event) => {
    if (event.pausedReason === PausedReason.AutoplayBlocked) {
      appTracker.trackEvent(`${eventPrefix}-autoplay-blocked`);
    } else if (event.type !== void 0) {
      appTracker.trackEvent(`${eventPrefix}-${event.type}`);
    }
  });
  session.once(CoreEvent.TimeRunningChange, (event) => {
    if (event.isTimeRunning) {
      appTracker.trackEvent(`${eventPrefix}-playing`);
    }
  });
  session.on(CoreEvent.WaitingChange, (event) => {
    if (event.isWaiting) {
      if (waiting !== void 0) {
        waiting.stillWaitingTimeout.destroy();
      }
      waiting = {
        reason: event.reason,
        startTime: Date.now(),
        // Report "still waiting" after some seconds.
        stillWaitingTimeout: destroyableTimeout(() => {
          const eventName = `${eventPrefix}-duration-${event.reason}-${STILL_WAITING_TIMEOUT}s`;
          appTracker.trackEvent(eventName, { duration: STILL_WAITING_TIMEOUT * 1e3 });
        }, STILL_WAITING_TIMEOUT * 1e3)
      };
      destroyables.addDestroyable(waiting.stillWaitingTimeout);
    } else {
      if (waiting?.reason !== event.reason) {
        waiting?.stillWaitingTimeout.destroy();
        waiting = void 0;
        return;
      }
      const duration = Date.now() - waiting.startTime;
      const eventName = `${eventPrefix}-duration-${waiting.reason}`;
      waiting.stillWaitingTimeout.destroy();
      waiting = void 0;
      appTracker.trackEvent(eventName, { duration });
    }
  });
  destroyables.addDestroyables([
    // Don't continue to track waiting time if the app changes visibility.
    destroyableEventListener2(document, "visibilitychange", () => {
      waiting?.stillWaitingTimeout.destroy();
      waiting = void 0;
    }),
    destroyableEventListener2(document, "webkitvisibilitychange", () => {
      waiting?.stillWaitingTimeout.destroy();
      waiting = void 0;
    }),
    // Track exits while waiting to see for how long people wait before exiting.
    beforeUnload(() => {
      if (waiting !== void 0) {
        waiting?.stillWaitingTimeout.destroy();
        const duration = Date.now() - waiting.startTime;
        appTracker.trackEvent(`${eventPrefix}-duration-${waiting.reason}-exit`, { duration });
      }
    })
  ]);
  session.on(CoreEvent.Broken, (event) => {
    const error = event.error;
    appTracker.trackEvent(`${eventPrefix}-broken`, { error });
    if (error) {
      appTracker.trackException(error, error.fatal, { cause: error.getCauseAsString() });
    }
  });
  session.once(CoreEvent.SessionClosed, () => {
    appTracker.flush();
  });
  return destroyables;
}

// src/integrations/player-core/app-insights-log-handler.ts
import { PlaybackSessionError } from "@nrk/player-core/types.js";
import { LogLevel } from "@nrk/player-core/utils.js";
var AppInsightsLogHandler = class {
  constructor(appTracker) {
    this.appTracker = appTracker;
  }
  log(namespace, level, message, ...messages) {
    if (namespace === "Metric") {
      this.appTracker.trackMetric(message, messages[0]);
      return;
    }
    if (namespace === "AppTracker" || namespace === "AppInsights") {
      return;
    }
    if (level === LogLevel.Error) {
      if (message instanceof PlaybackSessionError || messages.some((m) => m instanceof PlaybackSessionError)) {
        return;
      }
      this.appTracker.trackEvent("log-error", { namespace, message: `${message}`, log: [message, ...messages] });
    }
  }
};

// src/integrations/player-core/common-error-logging.ts
import { CoreEvent as CoreEvent2, PlaybackSessionErrorType } from "@nrk/player-core/types.js";
function logErrors(appTracker, session, info) {
  session.on(CoreEvent2.Broken, (event) => {
    const error = event.error;
    if (!error) {
      return;
    }
    const mediaURL = session.getMediaInfo().url;
    const parsedMediaURL = new URL(mediaURL);
    const errorCategory = {
      [PlaybackSessionErrorType.PLAYBACK_STOPPED_DECODE_ERROR]: "media" /* Media */,
      [PlaybackSessionErrorType.PLAYBACK_STOPPED_NETWORK_ERROR]: "network" /* Network */,
      [PlaybackSessionErrorType.NO_PLAYBACK_NETWORK_ERROR]: "network" /* Network */,
      [PlaybackSessionErrorType.NO_PLAYBACK_DECODE_ERROR]: "media" /* Media */,
      [PlaybackSessionErrorType.UNKNOWN_ERROR]: "unknown" /* Unknown */,
      [PlaybackSessionErrorType.UNSUPPORTED_FORMAT_ERROR]: "media" /* Media */
    }[error.type];
    const isLive = session.isLiveStream();
    appTracker.trackEvent("nativePlayerError", {
      Properties: Object.assign(
        {
          mediaUrlHost: parsedMediaURL.host,
          mediaUrlPath: parsedMediaURL.pathname,
          mediaUrlQuery: parsedMediaURL.search,
          playerTime: `${Math.floor(
            isLive ? session.getCurrentTimeLive().getTime() / 1e3 : session.getCurrentTime()
          )}`,
          playerIsRunningInBackground: "n/a",
          errorCategory,
          errorType: error.type,
          errorCode: "n/a",
          errorDescription: error.message,
          underlyingErrorType: error.causeSummary ?? "n/a",
          underlyingErrorCode: "n/a",
          underlyingErrorDescription: error.getCauseAsString()
        },
        info
      )
    });
  });
}

// src/integrations/player-core/psapi-progress.ts
import { DestroyableSet as DestroyableSet4, destroyableTimeout as destroyableTimeout2 } from "@nrk/destroyable";
import { CoreEvent as CoreEvent3 } from "@nrk/player-core/types.js";
function trackSessionProgress(session, tracker, originalPlaybackStartTime) {
  const destroyables = new DestroyableSet4();
  let progressTimeout;
  let trackingEnabled = false;
  const reportingInterval = 3e4;
  const notBefore = 120;
  let playbackStartTime = originalPlaybackStartTime;
  const clearTimeout2 = () => {
    if (progressTimeout !== void 0) {
      progressTimeout.destroy();
      progressTimeout = void 0;
    }
  };
  const sendProgressUpdate = () => {
    if (progressTimeout !== void 0) {
      progressTimeout.destroy();
      progressTimeout = void 0;
    }
    if (reportingInterval > 0) {
      progressTimeout = destroyableTimeout2(() => sendProgressUpdate(), reportingInterval);
      destroyables.addDestroyable(progressTimeout);
    }
    if (trackingEnabled) {
      tracker.reportPlaybackProgress(playbackStartTime, session.getCurrentTime());
    }
  };
  session.once(CoreEvent3.MediaRangeChange, async (_mediaRange) => {
    if (session.isLiveStream()) {
      return;
    }
    const eventTrack = session.createTimeRangeEventTrack();
    eventTrack.addRange({ start: notBefore, end: session.getScrubberRange().end });
    eventTrack.on("enter", (event) => {
      eventTrack.removeRange(event.range);
      trackingEnabled = true;
      sendProgressUpdate();
    });
    destroyables.addDestroyable(
      beforeUnload(() => {
        if (trackingEnabled) {
          tracker.reportPlaybackProgress(playbackStartTime, session.getCurrentTime());
        }
        clearTimeout2();
      })
    );
  });
  session.on(CoreEvent3.WaitingChange, (event) => {
    if (!event.isWaiting && event.reason === "seeking") {
      playbackStartTime = session.getCurrentTime();
    }
  });
  session.on(CoreEvent3.TimeRunningChange, (event) => {
    if (trackingEnabled) {
      if (event.isTimeRunning) {
        clearTimeout2();
        progressTimeout = destroyableTimeout2(() => sendProgressUpdate(), reportingInterval);
        destroyables.addDestroyable(progressTimeout);
      } else {
        tracker.reportPlaybackProgress(playbackStartTime, session.getCurrentTime());
        clearTimeout2();
      }
    }
  });
  session.on(CoreEvent3.Broken, () => {
    if (trackingEnabled) {
      tracker.reportPlaybackProgress(playbackStartTime, session.getCurrentTime());
    }
    clearTimeout2();
  });
  return destroyables;
}

// src/integrations/player-core/scores.ts
import { CoreEvent as CoreEvent5, PausedReason as PausedReason2 } from "@nrk/player-core/types.js";

// src/tracking-apis/scores-tracker.ts
var ScoresTracker = class {
  config;
  client;
  playerAdapter;
  stream;
  programId;
  constructor(library, config, playerAdapter) {
    this.config = config;
    this.client = new library(config.springStreamSite || "test");
    this.playerAdapter = playerAdapter;
    this.programId = config.springStreamProgramId !== "" ? config.springStreamProgramId : void 0;
  }
  /**
   * Start tracking. See the rules in the class description.
   */
  start() {
    if (!this.stream) {
      this.stream = this.client.track("nrk", this.createDescription(), this.createAdapter());
    }
  }
  /**
   * Stop tracking. See the rules in the class description.
   */
  stop() {
    if (this.stream) {
      this.stream.stop();
      this.stream = void 0;
    }
  }
  /**
   * Track changing programs in live streams which have an EPG by
   * calling this with the corresponding program ID. The program ID will be
   * undefined for gaps in the EPG or if the EPG is missing.
   */
  updateProgramId(programId) {
    if (this.programId !== programId) {
      this.programId = programId;
      if (this.stream) {
        this.stop();
        this.start();
      }
    }
  }
  get libraryVersion() {
    return this.client.version;
  }
  /**
   * Create a SpringStreams adapter based on a player adapter.
   */
  createAdapter() {
    return {
      getMeta: () => ({
        pl: this.playerAdapter.getPlayerName(),
        plv: this.playerAdapter.getPlayerVersion(),
        sx: window.screen.width,
        sy: window.screen.height
      }),
      getDuration: () => this.playerAdapter.isLive() ? 0 : Math.round(this.playerAdapter.getDuration()),
      getPosition: () => this.playerAdapter.isLive() ? this.playerAdapter.getCurrentLiveTime() : this.playerAdapter.getCurrentTime()
    };
  }
  /**
   * Create a SpringStreams media description.
   *
   * Missing program ID is plugged with channel ID, as requested by the NRK
   * analytics team.
   */
  createDescription() {
    return {
      stream: this.config.springStreamStream,
      ct: this.config.springStreamContentType,
      cq: this.programId?.toUpperCase() ?? this.playerAdapter.getChannelId()
    };
  }
};

// src/utils/epg-events.ts
import { CoreEvent as CoreEvent4 } from "@nrk/player-core/types.js";
import { getLogger as getLogger3 } from "@nrk/player-core/utils.js";
import { TimeWithTZ } from "@nrk/player-psapi-client";
import { EventEmitter } from "eventemitter3";
var logger3 = getLogger3("EPGEvents");
var TODAY = 0;
var TOMORROW = 1;
var NON_INDEX = -1;
var IMMIMENT_PERIOD = 180;
var EPGEvents = class extends EventEmitter {
  session;
  changeTrack;
  imminentTrack;
  updating = false;
  programExitTimeout;
  pausedEPGIndex;
  currentEPGIndex;
  entries;
  constructor(session) {
    super();
    this.session = session;
    this.session.on(CoreEvent4.TimeRunningChange, this.handleTimeRunningChange, this);
    this.changeTrack = session.createTimeRangeEventTrack();
    this.changeTrack.on("enter", this.handleProgramChange, this);
    this.changeTrack.on("exit", this.handleProgramChange, this);
    this.imminentTrack = session.createTimeRangeEventTrack();
    this.imminentTrack.on("enter", this.handleProgramStartImminent, this);
    this.entries = [];
  }
  /**
   * Returns the current EPG entry, if any.
   */
  getCurrentEntry() {
    return this.currentEPGIndex !== void 0 ? this.entries[this.currentEPGIndex] : void 0;
  }
  /**
   * Find the entry matching the time in the argument, undefined if not found
   */
  getEntryByTime(time) {
    if (this.entries.length === 0) {
      return;
    }
    for (const entry of this.entries) {
      if (entry.actualStart.utc <= time.utc && entry.actualEnd.utc >= time.utc) {
        return entry;
      }
    }
  }
  /**
   * Discard all current EPG entries and use the new entries instead. If the
   * stream hasn't started yet, it will wait in order for timestampToPosition()
   * and media range to become available.
   */
  async updateEPG(entries) {
    let timeRange = this.session.getMediaRange();
    if (timeRange.start === 0 && timeRange.end === 0) {
      logger3.log("Waiting for media range.");
      await new Promise((resolve) => this.session.once(CoreEvent4.MediaRangeChange, () => resolve()));
      timeRange = this.session.getMediaRange();
    }
    logger3.log("Updating EPG data.");
    this.updating = true;
    this.changeTrack.removeAllRanges();
    this.imminentTrack.removeAllRanges();
    this.entries = entries;
    for (let i = 0; i < entries.length - 1; ++i) {
      const end = Math.floor(entries[i].actualEnd.utc);
      const nextStart = Math.floor(entries[i + 1].actualStart.utc);
      if (nextStart - end > 0) {
        const gapEntry = {
          actualStart: new TimeWithTZ(entries[i].actualEnd.utc, entries[i].actualEnd.tzOffset),
          actualEnd: new TimeWithTZ(entries[i + 1].actualStart.utc, entries[i + 1].actualStart.tzOffset)
        };
        entries.splice(i + 1, 0, gapEntry);
      }
    }
    if (entries.length === 0) {
      const today = datesFor(TODAY);
      entries.push({
        actualStart: new TimeWithTZ(today[0].getTime(), today[0].getTimezoneOffset()),
        actualEnd: new TimeWithTZ(today[1].getTime(), today[1].getTimezoneOffset())
      });
      const tomorrow = datesFor(TOMORROW);
      entries.push({
        actualStart: new TimeWithTZ(tomorrow[0].getTime(), tomorrow[0].getTimezoneOffset()),
        actualEnd: new TimeWithTZ(tomorrow[1].getTime(), tomorrow[1].getTimezoneOffset())
      });
    }
    for (let i = 0; i < entries.length; ++i) {
      const program = entries[i];
      const programEndPosition = this.session.liveTimeToPosition(program.actualEnd.toDateObject());
      if (programEndPosition > timeRange.start) {
        const programStartPosition = this.session.liveTimeToPosition(program.actualStart.toDateObject());
        this.changeTrack.addRange({ start: programStartPosition, end: programEndPosition }, i);
        this.imminentTrack.addRange({ start: programStartPosition - IMMIMENT_PERIOD, end: programStartPosition }, i);
      }
    }
    this.updating = false;
    this.emit("epgupdated", entries);
    logger3.log("EPG update done.");
  }
  destroy() {
    if (this.programExitTimeout !== void 0) {
      clearTimeout(this.programExitTimeout);
      this.programExitTimeout = void 0;
    }
    this.changeTrack.destroy();
    this.imminentTrack.destroy();
    this.removeAllListeners();
  }
  /**
   * When time starts running, check if the EPG entry has changed while the
   * player was paused (pausedEPGIndex), and apply any change.
   */
  handleTimeRunningChange(event) {
    if (event.isTimeRunning && this.pausedEPGIndex !== void 0) {
      const epgIndex = this.pausedEPGIndex === NON_INDEX ? void 0 : this.pausedEPGIndex;
      this.pausedEPGIndex = void 0;
      if (this.currentEPGIndex !== epgIndex) {
        this.changeProgram(epgIndex);
      }
    }
  }
  /**
   * Handle 'enter' and 'exit' events from the program change time range event
   * track, with entry index (this.entries) as event data.
   */
  handleProgramChange(event) {
    if (this.programExitTimeout !== void 0) {
      clearTimeout(this.programExitTimeout);
      this.programExitTimeout = void 0;
    }
    const epgIndex = event.range.data;
    if (event.type === "enter" && this.updating && epgIndex === this.currentEPGIndex) {
      logger3.debug(`Got ${event.type} ${epgIndex}. Ignoring due to data update.`);
      return;
    }
    if (this.session.isTimeRunning()) {
      if (event.type === "enter") {
        logger3.debug(`Got ${event.type} ${epgIndex}. Changing to ${epgIndex}.`);
        this.changeProgram(epgIndex);
      } else if (this.currentEPGIndex === epgIndex) {
        logger3.debug(`Got ${event.type} ${epgIndex}. Changing to no program unless immediate enter.`);
        this.changeProgram(void 0);
      } else {
        logger3.debug(`Got ${event.type} ${epgIndex}. Ignoring due to late exit.`);
      }
    } else {
      if (event.type === "enter") {
        logger3.debug(`Got ${event.type} ${epgIndex}. Changing to ${epgIndex} when time starts.`);
        this.pausedEPGIndex = epgIndex;
        this.emit("programstartimminent", this.entries[epgIndex], 0);
      } else if (this.pausedEPGIndex === void 0 && this.currentEPGIndex === epgIndex) {
        logger3.debug(`Got ${event.type} ${epgIndex}. Changing to no program when time starts.`);
        this.pausedEPGIndex = NON_INDEX;
      } else if (this.pausedEPGIndex === epgIndex) {
        logger3.debug(`Got ${event.type} ${epgIndex}. Clearing next program.`);
        this.pausedEPGIndex = void 0;
      } else {
        logger3.debug(`Got ${event.type} ${epgIndex}. Ignoring due to late exit.`);
      }
    }
  }
  /**
   * Handle 'enter' events from the imminent time range event track, with entry
   * index (this.entries) as event data.
   */
  handleProgramStartImminent(event) {
    const epgIndex = event.range.data;
    const programStart = event.range.end;
    const currentTime = this.session.getCurrentTime();
    this.emit("programstartimminent", this.entries[epgIndex], Math.max(0, programStart - currentTime));
  }
  /**
   * Emit programchange events. 'exit' events (programchange with undefined
   * program ID) are not emitted when immediately followed by 'enter' events.
   * They are only emitted when there are gaps in the EPG.
   */
  changeProgram(epgIndex) {
    if (epgIndex !== void 0) {
      this.currentEPGIndex = epgIndex;
      const currentEntry = this.getCurrentEntry();
      logger3.log(
        `Program changed: ${isEPGEntry(currentEntry) ? currentEntry.programId : "unknown"} - ${isEPGEntry(currentEntry) ? currentEntry.title : "unknown"}`
      );
      this.emit("programchange", currentEntry);
    } else {
      this.programExitTimeout = setTimeout(() => {
        this.currentEPGIndex = void 0;
        logger3.log("Program changed: No program.");
        this.emit("programchange");
      }, 100);
    }
  }
};
function isEPGEntry(entry) {
  if (!entry) {
    return false;
  }
  return Object.prototype.hasOwnProperty.call(entry, "programId");
}
function datesFor(daysToAdd) {
  const today = /* @__PURE__ */ new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + daysToAdd);
  return [new Date(tomorrow.setHours(0, 0, 0, 0)), new Date(tomorrow.setHours(23, 59, 59, 0))];
}

// src/integrations/player-core/scores.ts
function trackScoresSession(session, library, setup, epgEvents) {
  const adapter = createScoresAdapter(session, setup);
  const tracker = new ScoresTracker(library, setup.config, adapter);
  const unloadListener = beforeUnload(() => tracker.stop());
  session.on(CoreEvent5.Broken, () => tracker.stop());
  session.on(CoreEvent5.SessionClosed, () => tracker.stop());
  epgEvents?.on("programchange", (entry) => {
    if (isEPGEntry(entry)) {
      tracker.updateProgramId(entry?.programId);
    }
  });
  session.on(CoreEvent5.PlayingChange, (event) => {
    if (event.isPlaying) {
      if (event.type !== void 0) {
        tracker.start();
      }
    } else {
      if (event.pausedReason === PausedReason2.Ended) {
        tracker.stop();
      }
    }
  });
  if (session.isPlaying()) {
    tracker.start();
  }
  return { destroyable: unloadListener, libraryVersion: tracker.libraryVersion };
}
function createScoresAdapter(session, setup) {
  return {
    getPlayerName: () => setup.playerName,
    getPlayerVersion: () => setup.playerVersion,
    getChannelId: () => setup.channelId,
    isLive: () => session.isLiveStream(),
    getDuration: () => session.getMediaRange().end,
    getCurrentTime: () => session.getCurrentTime(),
    getCurrentLiveTime: () => session.getCurrentTimeLive().getTime() / 1e3
  };
}

// src/integrations/player-core/snowplow.ts
import { destroyableEventListener as destroyableEventListener3, DestroyableSet as DestroyableSet5, destroyableTimeout as destroyableTimeout3 } from "@nrk/destroyable";
import { CoreEvent as CoreEvent6 } from "@nrk/player-core/types.js";
import { getLogger as getLogger4 } from "@nrk/player-core/utils.js";
import { isEPGEntry as isEPGEntry2 } from "@nrk/player-psapi-client";

// src/utils/time-with-tz.ts
var ROTATION = 60;
var RADIX = 10;
var pad = (n) => n < 10 ? `0${n}` : n;
var TimeWithTZ2 = class _TimeWithTZ {
  /**
   * Milliseconds since epoch.
   */
  utc;
  /**
   * Time zone offset in minutes.
   */
  tzOffset;
  /**
   * @param utc Milliseconds since epoch.
   * @param tzOffset Time zone offset in minutes.
   */
  constructor(utc, tzOffset) {
    this.utc = utc;
    this.tzOffset = tzOffset;
  }
  /**
   * Parses the '/Date(<ms since epoch><time zone>)/' format and returns an
   * instance of the class, if successful.
   */
  static parse(timestamp) {
    const pattern = /^\/Date\((\d+)(?:([+-]\d{2})(\d{2}))?\)\/$/;
    const result = pattern.exec(timestamp);
    if (result) {
      const utc = parseInt(result[1], RADIX);
      const tzOffset = result.length > 2 ? parseInt(result[2], RADIX) * ROTATION + parseInt(result[3], RADIX) : 0;
      return new _TimeWithTZ(utc, tzOffset);
    }
    throw new Error(`Unknown format: ${timestamp}`);
  }
  /**
   * Returns an ISO 8601 timestamp looking like 'YYYY-mm-ddTHH:MM:SS+ZZ:ZZ'.
   * Milliseconds are ignored.
   */
  toModernPSAPIFormat() {
    const date = new Date(this.utc + this.tzOffset * ROTATION * 1e3);
    return [
      date.getUTCFullYear(),
      "-",
      pad(date.getUTCMonth() + 1),
      "-",
      pad(date.getUTCDate()),
      "T",
      pad(date.getUTCHours()),
      ":",
      pad(date.getUTCMinutes()),
      ":",
      pad(date.getUTCSeconds()),
      this.tzOffset < 0 ? "-" : "+",
      pad(Math.floor(this.tzOffset / ROTATION)),
      ":",
      pad(this.tzOffset % ROTATION)
    ].join("");
  }
  /**
   * Returns a compact date format (YYYMMDD).
   */
  toCompactLocalDate() {
    const date = new Date(this.utc + this.tzOffset * ROTATION * 1e3);
    return [date.getUTCFullYear(), pad(date.getUTCMonth() + 1), pad(date.getUTCDate())].join("");
  }
  /**
   * Returns a Date object without the time zone information.
   */
  toDateObject() {
    return new Date(this.utc);
  }
};

// src/integrations/player-core/snowplow.ts
var logger4 = getLogger4("SnowplowTracker");
var TWENTYNINE_MINUTES = 29 * 60 * 1e3;
var UNKNOWN_ID = "unknown";
function TrackSnowplowSession(session, tracker, mediaConfig, trackingSession, epgEvents) {
  const destroyables = new DestroyableSet5();
  let currentEPGEntry = void 0;
  let finalCompleteHasBeenSent = false;
  let finalExitHasBeenSent = false;
  let initialStartEventSent = false;
  let sendPeriodicEventTimeout;
  let startOfRange = 0;
  let lastProgressEnd = -1;
  const mediaId = mediaConfig.mediaId;
  const clearTimeout2 = () => {
    if (sendPeriodicEventTimeout !== void 0) {
      sendPeriodicEventTimeout.destroy();
      sendPeriodicEventTimeout = void 0;
    }
  };
  destroyables.addDestroyable(
    destroyableEventListener3(
      document,
      "player.playNextEpisode",
      () => {
        if (finalCompleteHasBeenSent || !initialStartEventSent) {
          return;
        }
        let currentTime = currentTimeInSessionOrProgram(session, currentEPGEntry);
        logger4.info(`event 'complete' on playNextEpisode ${startOfRange} ${currentTime}`);
        if (currentTime === 0 || currentTime < startOfRange) {
          currentTime = startOfRange;
        }
        finalCompleteHasBeenSent = sendCompleteEvent(startOfRange, currentTime, isPlaybackLive(session));
      },
      { once: true }
    )
  );
  session.once(CoreEvent6.TimeRunningChange, () => {
    startOfRange = currentTimeInSessionOrProgram(session, currentEPGEntry);
    if (!session.isLiveStream()) {
      sendInitialStartForOdm();
    }
    if (session.isLiveStream() && hasNoEpg()) {
      sendInitialStartForLive2Vod();
    }
    session.on(CoreEvent6.PlayingChange, (playingChangeEvent) => {
      if (!playingChangeEvent.isPlaying && playingChangeEvent.pausedReason === "ended" && !finalCompleteHasBeenSent) {
        const currentTime = currentTimeInSessionOrProgram(session, currentEPGEntry);
        logger4.debug(`event 'complete' on ended ${startOfRange} ${currentTime}`);
        finalCompleteHasBeenSent = sendCompleteEvent(startOfRange, currentTime, isPlaybackLive(session));
      }
      if (session.isLiveStream()) {
        if (hasNoEpg()) {
          onPlayingChangeLive2Vod(playingChangeEvent);
        } else {
          onPlayingChangeLive(playingChangeEvent);
        }
      } else {
        onPlayingChangeOndemand(playingChangeEvent);
      }
    });
    session.on(CoreEvent6.TimeRunningChange, (timerunningChangeEvent) => {
      const isPlaying = session.isPlaying();
      const pausedReason = session.getPausedReason();
      if (session.isLiveStream()) {
        if (hasNoEpg()) {
          onTimeRunningChangeLive2Vod(timerunningChangeEvent, isPlaying, pausedReason);
        } else {
          onTimeRunningChangeLive(timerunningChangeEvent, isPlaying, pausedReason);
        }
      } else {
        onTimeRunningChangeOnDemand(timerunningChangeEvent, isPlaying, pausedReason);
      }
    });
  });
  epgEvents?.on("programchange", (entry) => {
    if (!entry || session.isLiveStream() && hasNoEpg()) {
      return;
    }
    const previousEPGEntry = currentEPGEntry;
    currentEPGEntry = entry;
    const currentTime = currentTimeInSessionOrProgram(session, currentEPGEntry);
    if (currentEPGEntry?.actualEnd?.utc < session.getCurrentTimeLive().getTime()) {
      return;
    }
    if (!initialStartEventSent) {
      sendInitialStartEventForLive(entry, currentTime);
    } else {
      if (session.isTimeRunning() && lastProgressEnd === -1) {
        sendCompleteAndStart(previousEPGEntry, entry, currentTime);
      } else if (session.isTimeRunning()) {
        sendExitAndStart(previousEPGEntry, entry, currentTime);
      }
    }
  });
  session.on(CoreEvent6.SessionClosed, () => {
    const currentTime = currentTimeInSessionOrProgram(session, currentEPGEntry);
    if (!finalCompleteHasBeenSent && !finalExitHasBeenSent) {
      if (!session.isTimeRunning() && startOfRange > currentTime) {
        finalExitHasBeenSent = sendExitEvent(currentTime, currentTime, "session close", isPlaybackLive(session));
        return;
      }
      if (session.isTimeRunning() && startOfRange > currentTime && currentTime === 0) {
        finalExitHasBeenSent = sendExitEvent(startOfRange, startOfRange, "session close", isPlaybackLive(session));
        return;
      }
      if (startOfRange > currentTime) {
        return trackException("exit", startOfRange, currentTime, session.getCurrentTime(), "session close");
      }
      finalExitHasBeenSent = sendExitEvent(startOfRange, currentTime, "session close", isPlaybackLive(session));
    }
    clearTimeout2();
  });
  destroyables.addDestroyable(
    beforeUnload(() => {
      if (!finalCompleteHasBeenSent && !finalExitHasBeenSent) {
        const currentTime = currentTimeInSessionOrProgram(session, currentEPGEntry);
        if (!session.isTimeRunning() && startOfRange > currentTime) {
          sendExitEvent(currentTime, currentTime, "unload", isPlaybackLive(session));
          return;
        }
        if (session.isTimeRunning() && startOfRange > currentTime && currentTime === 0) {
          sendExitEvent(startOfRange, startOfRange, "unload", isPlaybackLive(session));
          return;
        }
        if (startOfRange > currentTime) {
          return trackException("exit", startOfRange, currentTime, session.getCurrentTime(), "unload");
        }
        sendExitEvent(startOfRange, currentTime, "unload", isPlaybackLive(session));
      }
      clearTimeout2();
    })
  );
  return destroyables;
  function hasNoEpg() {
    return !epgEvents || epgEvents.entries.length === 0;
  }
  function sendInitialStartForLive2Vod() {
    setMedia(mediaId, {
      kind: mediaConfig.contentKind,
      channelId: mediaConfig.channelId,
      source: mediaConfig.contentSource
    });
    const currentTime = Math.floor(session.getCurrentTime());
    logger4.debug(`event 'start' for live2vod ${mediaId} ${currentTime}`);
    tracker.sendStartEvent(startOfRange, false);
    initialStartEventSent = true;
    resetPeriodicEventTimeout();
    startOfRange = currentTime;
    tracker.setMediaAutoplay(false);
  }
  function sendInitialStartForOdm() {
    setMedia(mediaId, {
      kind: mediaConfig.contentKind,
      channelId: mediaConfig.channelId,
      source: mediaConfig.contentSource
    });
    logger4.debug(`event 'start' for ODM ${startOfRange}`);
    tracker.sendStartEvent(startOfRange, false);
    initialStartEventSent = true;
    tracker.setMediaAutoplay(false);
    resetPeriodicEventTimeout();
  }
  function sendInitialStartEventForLive(entry, currentTime) {
    setMedia(programIdFromEntry(entry), {
      kind: mediaConfig.contentKind,
      channelId: mediaConfig.channelId,
      source: mediaConfig.contentSource
    });
    logger4.debug(`initial 'start' event on epg change ${programIdFromEntry(entry)} ${currentTime}`);
    tracker.sendStartEvent(currentTime, isPlaybackLive(session));
    resetPeriodicEventTimeout();
    initialStartEventSent = true;
    startOfRange = currentTime;
    tracker.setMediaAutoplay(false);
  }
  function sendExitAndStart(previousEPGEntry, entry, currentProgramTime) {
    sendExitEvent(
      lastProgressEnd,
      lastProgressEnd,
      `epg change ${programIdFromEntry(previousEPGEntry)}`,
      isPlaybackLive(session)
    );
    resetPeriodicEventTimeout();
    lastProgressEnd = -1;
    setMedia(programIdFromEntry(entry), {
      kind: mediaConfig.contentKind,
      channelId: mediaConfig.channelId,
      source: mediaConfig.contentSource
    });
    logger4.debug(`event 'start' on epg change ${programIdFromEntry(entry)} ${currentProgramTime}`);
    tracker.sendStartEvent(currentProgramTime, isPlaybackLive(session));
    startOfRange = currentProgramTime;
  }
  function sendCompleteAndStart(previousEPGEntry, entry, currentProgramTime) {
    const previousProgramTime = currentTimeInSessionOrProgram(session, previousEPGEntry) === 0 ? startOfRange : currentTimeInSessionOrProgram(session, previousEPGEntry);
    if (initialStartEventSent) {
      logger4.debug(
        `event 'complete' on epg change ${programIdFromEntry(previousEPGEntry)} -> ${programIdFromEntry(entry)}  ${startOfRange} ${previousProgramTime}`
      );
      sendCompleteEvent(startOfRange, previousProgramTime, isPlaybackLive(session));
    }
    setMedia(programIdFromEntry(entry), {
      kind: mediaConfig.contentKind,
      channelId: mediaConfig.channelId,
      source: mediaConfig.contentSource
    });
    logger4.debug(`event 'start' on epg change ${programIdFromEntry(entry)} ${currentProgramTime}`);
    tracker.sendStartEvent(currentProgramTime, isPlaybackLive(session));
    resetPeriodicEventTimeout();
    startOfRange = currentProgramTime;
    if (!initialStartEventSent) {
      initialStartEventSent;
    }
  }
  function resetPeriodicEventTimeout() {
    clearTimeout2();
    sendPeriodicEventTimeout = destroyableTimeout3(sendPeriodicEvent, TWENTYNINE_MINUTES);
  }
  function setMedia(mediaIdArg, details) {
    if (mediaIdArg && mediaIdArg !== UNKNOWN_ID) {
      tracker.setMedia(mediaIdArg, { kind: details?.kind, source: details?.source, channelId: details?.channelId });
    } else {
      tracker.setUnknownMedia(mediaConfig.channelId);
    }
  }
  function onPlayingChangeOndemand(event) {
    if (event.isPlaying) {
      const currentTime = currentTimeInSessionOrProgram(session, currentEPGEntry);
      logger4.debug(`event 'start' on PlayingChange:play (ODM) ${currentTime}`);
      tracker.sendStartEvent(currentTime, isPlaybackLive(session));
      resetPeriodicEventTimeout();
      startOfRange = currentTime;
    }
  }
  function onPlayingChangeLive(event) {
    const currentProgramTime = currentTimeInSessionOrProgram(session, currentEPGEntry);
    const eventEPGEntry = epgEvents?.getEntryByTime(currentTimeLiveAsTimeWithTZ(session));
    if (!initialStartEventSent) {
      setMedia(programIdFromEntry(currentEPGEntry), {
        kind: mediaConfig.contentKind,
        channelId: mediaConfig.channelId,
        source: mediaConfig.contentSource
      });
    }
    if (event.isPlaying) {
      if (programIdFromEntry(eventEPGEntry) === programIdFromEntry(currentEPGEntry)) {
        logger4.debug(`event 'start' on PlayingChange:play (live) ${currentProgramTime}`);
        tracker.sendStartEvent(currentProgramTime, isPlaybackLive(session));
        resetPeriodicEventTimeout();
        startOfRange = currentProgramTime;
      }
    } else {
      if (programIdFromEntry(eventEPGEntry) !== programIdFromEntry(currentEPGEntry)) {
        startOfRange = currentProgramTime;
      }
    }
  }
  function onPlayingChangeLive2Vod(event) {
    const currentTime = currentTimeInSessionOrProgram(session, currentEPGEntry);
    if (event.isPlaying) {
      logger4.debug(`event 'start' on PlayingChange:play (live2vod) ${currentTime}`);
      tracker.sendStartEvent(currentTime, isPlaybackLive(session));
      resetPeriodicEventTimeout();
      startOfRange = currentTime;
    }
  }
  function onTimeRunningChangeOnDemand(event, isPlaying, pausedReason) {
    if (event.isTimeRunning && isPlaying) {
      startOfRange = currentTimeInSessionOrProgram(session, currentEPGEntry);
    } else if (!event.isTimeRunning && !pausedReason) {
      const eventTime = eventTimeInSessionOrProgram(event.time, session, currentEPGEntry);
      if (startOfRange > eventTime) {
        logger4.debug(`event 'progress' on TimeRunningChange:pause (ODM) ${startOfRange} ${startOfRange}`);
        tracker.sendProgressEvent(startOfRange, startOfRange, isPlaybackLive(session));
        if (eventTime !== 0) {
          startOfRange = eventTime;
        }
        resetPeriodicEventTimeout();
        return;
      }
      if (!finalCompleteHasBeenSent) {
        logger4.debug(`event 'progress' on TimeRunningChange:pause (ODM) ${startOfRange} ${eventTime}`);
        tracker.sendProgressEvent(startOfRange, eventTime, isPlaybackLive(session));
        resetPeriodicEventTimeout();
        startOfRange = currentTimeInSessionOrProgram(session, currentEPGEntry);
      }
    }
  }
  function onTimeRunningChangeLive(event, isPlaying, pausedReason) {
    if (event.isTimeRunning && isPlaying) {
      lastProgressEnd = -1;
      startOfRange = currentTimeInSessionOrProgram(session, currentEPGEntry);
    } else if (!event.isTimeRunning && !pausedReason) {
      const eventTime = eventTimeInSessionOrProgram(event.time, session, currentEPGEntry);
      logger4.debug(`event 'progress' on TimeRunningChange:pause (live) ${startOfRange} ${eventTime}`);
      if (startOfRange > eventTime) {
        trackException("progress", startOfRange, eventTime, session.getCurrentTime(), "TimeRunningChange:pause (live)");
        return;
      }
      tracker.sendProgressEvent(startOfRange, eventTime, isPlaybackLive(session));
      resetPeriodicEventTimeout();
      lastProgressEnd = eventTime;
      startOfRange = currentTimeInSessionOrProgram(session, currentEPGEntry);
    }
  }
  function onTimeRunningChangeLive2Vod(event, isPlaying, pausedReason) {
    if (event.isTimeRunning && isPlaying) {
      lastProgressEnd = -1;
      startOfRange = currentTimeInSessionOrProgram(session, currentEPGEntry);
    } else if (!event.isTimeRunning && !pausedReason) {
      const eventTime = eventTimeInSessionOrProgram(event.time, session, currentEPGEntry);
      logger4.debug(`event 'progress' on TimeRunningChange:pause (live2vod) ${startOfRange} ${eventTime}`);
      if (startOfRange > eventTime) {
        trackException(
          "progress",
          startOfRange,
          eventTime,
          session.getCurrentTime(),
          "TimeRunningChange:pause (live2vod)"
        );
        return;
      }
      tracker.sendProgressEvent(startOfRange, eventTime, isPlaybackLive(session));
      resetPeriodicEventTimeout();
      lastProgressEnd = eventTime;
      startOfRange = currentTimeInSessionOrProgram(session, currentEPGEntry);
    }
  }
  function sendPeriodicEvent() {
    const currentTime = currentTimeInSessionOrProgram(session, currentEPGEntry);
    if (!session.isTimeRunning() && startOfRange > currentTime) {
      logger4.debug(`event 'progress' periodic interval (custom) ${currentTime} ${currentTime}`);
      tracker.sendProgressEvent(currentTime, currentTime, isPlaybackLive(session));
      startOfRange = currentTime;
      resetPeriodicEventTimeout();
      return;
    }
    if (startOfRange > currentTime) {
      return trackException("progress", startOfRange, currentTime, session.getCurrentTime(), "periodic");
    }
    logger4.debug(`event 'progress' on periodic interval ${startOfRange} ${currentTime}`);
    tracker.sendProgressEvent(startOfRange, currentTime, isPlaybackLive(session));
    startOfRange = currentTime;
    resetPeriodicEventTimeout();
  }
  function sendCompleteEvent(startTime, endTime, live) {
    tracker.sendCompleteEvent(startTime, endTime, live);
    return true;
  }
  function sendExitEvent(startTime, endTime, context, live) {
    if (!initialStartEventSent) {
      return false;
    }
    if (startTime > endTime) {
      return trackException("exit", startTime, endTime, session.getCurrentTime(), "exit");
    }
    logger4.debug(`event 'exit' on ${context} ${startTime} ${endTime}`);
    tracker.sendExitEvent(startTime, endTime, live);
    return true;
  }
  function trackException(eventName, startTime, endTime, currentTime, context) {
    const error = new Error("Invalid end time");
    trackingSession.appTracker?.trackException(error, false, {
      id: mediaId,
      eventName,
      startTime,
      endTime,
      stack: error.stack,
      context,
      currentTime,
      at: (/* @__PURE__ */ new Date()).toString()
    });
    return false;
  }
  function currentTimeInSessionOrProgram(sessionArg, epgEntry) {
    if (!sessionArg.isLiveStream()) {
      return Math.max(0, Math.floor(sessionArg.getCurrentTime()));
    }
    if (epgEntry) {
      return findRelativeProgramTime(sessionArg.getCurrentTimeLive(), epgEntry.actualStart);
    }
    return Math.max(0, Math.floor(sessionArg.liveTimeToPosition(sessionArg.getCurrentTimeLive())));
  }
  function currentTimeLiveAsTimeWithTZ(sessionArg) {
    return new TimeWithTZ2(sessionArg.getCurrentTimeLive().getTime(), (/* @__PURE__ */ new Date()).getTimezoneOffset());
  }
  function eventTimeInSessionOrProgram(eventTime, sessionArg, epgEntry) {
    if (!sessionArg.isLiveStream()) {
      return Math.max(0, Math.floor(eventTime));
    }
    if (epgEntry) {
      return findRelativeProgramTimefromEventTime(eventTime, sessionArg, epgEntry.actualStart);
    }
    return eventTime === 0 ? Math.max(0, Math.floor(sessionArg.liveTimeToPosition(sessionArg.getCurrentTimeLive()))) : Math.max(0, Math.floor(eventTime));
  }
}
function isPlaybackLive(session) {
  return session.isLiveStream();
}
function findRelativeProgramTimefromEventTime(eventTime, session, actualStart) {
  return Math.max(0, Math.floor((session.positionToLiveTime(eventTime).getTime() - actualStart.utc) / 1e3));
}
function findRelativeProgramTime(currentTimeLive, actualStart) {
  return Math.max(0, Math.floor((currentTimeLive.getTime() - actualStart.utc) / 1e3));
}
function programIdFromEntry(entry) {
  if (!entry) {
    return UNKNOWN_ID;
  }
  return isEPGEntry2(entry) ? entry.programId : UNKNOWN_ID;
}

// src/integrations/player-core/index.ts
var logger5 = getLogger5("PlayerTracker");
var DEFAULT_PLAYER_FAMILY = "player-core";
var DEFAULT_PLAYER_PLATFORM = "web";
var sharedAppInsights;
var sharedAppTracker;
var PlayerTracker = class {
  playerInfo;
  site;
  parentURL;
  aiDiagnosticsId;
  disableAppInsights;
  scoresLibrary;
  /**
   * Shared AppTracker for all players on the page (copy of global variable),
   * used when there is no playback session. Playback sessions are tracked by
   * tracking sessions with their own specific AppTracker instances. But all of
   * them are using the same shared AppInsights instance.
   */
  appTracker;
  /**
   * Injected Snowplow bundle
   */
  snowplowBundle;
  constructor(playerInfo, setup, snowplowBundle) {
    this.playerInfo = playerInfo;
    this.snowplowBundle = snowplowBundle;
    const { aiDiagnosticsId, scoresLibrary, parentURL } = setup;
    this.site = new Site(parentURL ? { parentURL } : void 0);
    this.parentURL = parentURL;
    this.aiDiagnosticsId = aiDiagnosticsId;
    this.scoresLibrary = scoresLibrary;
    this.disableAppInsights = setup.disableAppInsights;
    if (!this.disableAppInsights) {
      const instrumentationKey = {
        // test: 'd4e0f7d6-9fdb-42e9-b8f8-91ff288d01af',
        // preprod: '10ccf025-3b42-4d82-b84d-901f21262a70',
        stage: "f4cf2c52-4917-43bd-b257-d82b0a681e18",
        prod: "ff70c9d2-d463-4e98-acb2-b1862bbddde1"
      };
      if (sharedAppInsights === void 0) {
        logger5.log("Creating shared Application Insights instance for site:", this.site.fullName);
        sharedAppInsights = new AppInsights(
          this.site.isProduction ? instrumentationKey.prod : instrumentationKey.stage,
          {}
        );
      }
      if (sharedAppTracker === void 0) {
        logger5.log("Creating shared Application Insights tracker.");
        sharedAppTracker = new AppTracker(sharedAppInsights, playerInfo.name, {
          fixedProperties: {
            applicationVersion: playerInfo.version,
            applicationFamily: playerInfo.family ?? DEFAULT_PLAYER_FAMILY,
            hostname: window.location.hostname,
            site: this.site.fullName,
            parentURL,
            diagnosticsId: aiDiagnosticsId
          }
        });
        logger5.log("Creating shared AppInsightsLogHandler.");
        addLogHandler(new AppInsightsLogHandler(sharedAppTracker));
      }
      this.appTracker = sharedAppTracker;
    }
  }
  trackSession(session, psapiClient, config, epgEvents) {
    const sessionId = cuid2();
    const destroyables = new DestroyableSet6();
    const playedTimeCollector = createPlayedTimeCollector(session);
    const trackingSession = { playedTimeCollector };
    let scoresLibraryVersion;
    destroyables.addDestroyable(playedTimeCollector);
    session.on(CoreEvent7.SessionClosed, () => destroyables.destroy());
    if (this.snowplowBundle && config.snowplow && config.snowplow.appId && config.snowplow.endpointURL) {
      logger5.log("Tracking session with Snowplow.", config.snowplow);
      trackingSession.snowplowTracker = new NrkPlayerTracker(this.snowplowBundle, {
        collectorUrl: config.snowplow.endpointURL,
        appId: config.snowplow.appId,
        appVersion: this.playerInfo.version,
        appBuild: this.playerInfo.build,
        environment: this.site.isProduction ? "prod" : this.site.environment,
        serviceId: config.snowplow.serviceId,
        platform: config.snowplow.platform,
        disableLocalStorage: true
      });
      destroyables.addDestroyable(
        TrackSnowplowSession(
          session,
          trackingSession.snowplowTracker,
          {
            mediaId: config.snowplow.mediaId,
            contentKind: config.snowplow.contentKind,
            contentSource: config.snowplow.contentSource,
            autoplay: config.snowplow.autoplay,
            channelId: config.snowplow.channelId,
            startTime: config.snowplow.startTime,
            startTimeLive: config.snowplow.startTimeLive
          },
          trackingSession,
          epgEvents
        )
      );
    }
    if (config.progress) {
      logger5.log("Tracking session with PSAPI progress tracker.");
      trackingSession.progressTracker = new ProgressTracker(
        config.progress.programId,
        config.progress.playbackStartTime,
        config.progress.trackingCallback
      );
      destroyables.addDestroyable(
        trackSessionProgress(session, trackingSession.progressTracker, config.progress.playbackStartTime)
      );
    }
    if (config.scores && config.scores.config && this.scoresLibrary) {
      logger5.log("Tracking session with TNS Scores.");
      const isChannel = psapiClient.metadata?.availability.live?.type === "channel";
      const channelId = isChannel ? psapiClient.manifest?.id ?? psapiClient.metadata?.id : void 0;
      const scoresSession = trackScoresSession(
        session,
        this.scoresLibrary,
        {
          playerName: this.playerInfo.name,
          playerVersion: this.playerInfo.version,
          config: config.scores.config,
          channelId
        },
        epgEvents
      );
      scoresLibraryVersion = scoresSession.libraryVersion;
      destroyables.addDestroyable(scoresSession.destroyable);
    }
    if (!this.disableAppInsights) {
      logger5.log("Tracking session with App Insights.");
      const appTracker = new AppTracker(sharedAppInsights, this.playerInfo.name, {
        fixedProperties: {
          applicationVersion: this.playerInfo.version,
          applicationFamily: this.playerInfo.family ?? DEFAULT_PLAYER_FAMILY,
          hostname: window.location.hostname,
          site: this.site.fullName,
          parentURL: this.parentURL,
          trackingSession: sessionId,
          // Redundant now that we set the session ID tag as well...
          diagnosticsId: this.aiDiagnosticsId,
          versions: {
            PlayerCore: playerCoreVersion,
            SpringStreams: scoresLibraryVersion ?? "none"
          }
        },
        dynamicProperties: () => {
          const timestamp = session.getCurrentTimeLive();
          const time = timestamp !== void 0 ? timestamp.getTime() / 1e3 : session.getCurrentTime();
          return {
            mediaInfo: session.getMediaInfo(),
            adapterInfo: session.getAdapterInfo(),
            documentHidden: "webkitHidden" in document ? document.webkitHidden : document.hidden,
            documentReadyState: document.readyState,
            playback: {
              time,
              position: session.getCurrentTime(),
              quality: session.getCurrentQuality(),
              rate: session.getPlaybackRate(),
              state: session.getState(),
              waitingReason: session.getWaitingReason(),
              pausedReason: session.getPausedReason(),
              mediaRange: session.getMediaRange()
            },
            displayModes: {
              pip: session.getPiPState(),
              fullscreen: session.getFullscreenState(),
              airPlay: session.getAirPlayState()
            }
          };
        },
        fixedTags: {
          sessionId
        }
      });
      trackingSession.appTracker = appTracker;
      destroyables.addDestroyable(trackAppInsightsSession(session, appTracker));
      destroyables.addDestroyable(appTracker);
      if (config.metadata) {
        logger5.log("Tracking playback errors.");
        const playerInfo = this.playerInfo;
        logErrors(appTracker, session, {
          platform: playerInfo.platform ?? DEFAULT_PLAYER_PLATFORM,
          applicationName: playerInfo.name,
          applicationVersion: playerInfo.version,
          ...config.metadata
        });
      }
    }
    return trackingSession;
  }
};
function createPlayedTimeCollector(session) {
  const playedTimeCollector = new PlayedTimeCollector();
  session.on(CoreEvent7.TimeRunningChange, (event) => {
    if (event.isTimeRunning) {
      playedTimeCollector.timeStarted(event.time);
    } else {
      playedTimeCollector.timeStopped(event.time);
    }
  });
  session.on(CoreEvent7.TimeUpdate, (time) => {
    playedTimeCollector.timeUpdate(time);
  });
  return playedTimeCollector;
}
export {
  AppInsights,
  AppTracker,
  DEFAULT_ENDPOINT,
  DEFAULT_PATH,
  DataPointType,
  EPGEvents,
  PlayedTimeCollector,
  PlayerTracker,
  ProgressTracker,
  ScoresTracker,
  SeverityLevel,
  Site,
  clipRanges,
  isEPGEntry,
  mergeOverlappingRanges,
  siteConfigs,
  summarizeRanges,
  translateTagKey,
  translateTagKeys
};
